/**
 * @fileoverview Custom hook for managing the analysis wizard state and flow
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import deepCutApi from '../services/deepcutApi.js';
import Utils from '../../../models/helper/Utils';
import { ALWAYS_SELECT_CORE_CHIPS } from '../constants/schema.js';
// @ts-ignore
import ti18n from 'i18n!nls/Deepcuti18n';

/**
 * @typedef {import('../types/deepcut.js').AnalysisResults} AnalysisResults
 * @typedef {import('../types/deepcut.js').UserFeedback} UserFeedback
 */

/**
 * @typedef {'goal'|'patternReview'|'questions'|'plan'} WizardStep
 */

const CONFIDENCE_THRESHOLD = 0.8;
const EMPTY_PLAN = [];

/**
 * Determines if a chip should be selected by default
 * @param {Object} chip - The editing chip
 * @returns {boolean}
 */
function chipSelectedByDefault(chip) {
  if (ALWAYS_SELECT_CORE_CHIPS && Utils.getInstance()?.getPreselectedDeepCutChips()?.includes(chip.chipLabel)) {
    return true;
  }
  const confidence = typeof chip.confidence === 'number' ? chip.confidence : 1;
  return confidence >= CONFIDENCE_THRESHOLD;
}

/**
 * Checks if a response contains an updated plan
 * @param {any} data - Response data to check
 * @returns {boolean}
 */
function isUpdatePlanResponse(data) {
  return data && typeof data === 'object' && 'updatedPlan' in data;
}

/**
 * Gets the intent label from the intent code using cached intents
 * @param {string} intentCode - The intent code to look up
 * @param {Array} suggestedIntents - Array of suggested intents with code and label
 * @returns {string} The intent label or the code if not found
 */
function getIntentLabel(intentCode, suggestedIntents) {
  if (!intentCode || intentCode === 'custom') {
    return intentCode;
  }
  
  const intent = suggestedIntents.find(intent => intent.code === intentCode);
  return intent ? intent.label : intentCode;
}

/**
 * Custom hook for managing analysis wizard state and navigation
 * @param {Object} props - Hook props
 * @param {AnalysisResults} props.results - Initial analysis results
 * @param {string} props.transcript - The transcript text
 * @param {number|null} props.transcriptId - ID of the transcript (if saved)
 * @param {Function} props.onClose - Callback for closing the wizard
 * @param {Function} props.onApply - Callback for applying the final plan
 * @returns {Object} Wizard state and methods
 */
export function useAnalysisWizard({
  results,
  transcript,
  transcriptId,
  projectId,
  onClose,
  onApply,
  isEditLikeMeMode = false,
}) {
  // Handle null results case
  const safeResults = results || {
    selectedIntent: '',
    detectedContentType: '',
    suggestedIntents: [],
    probingQuestions: [],
    plan: []
  };

  // Ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Step management
  const [currentStep, setCurrentStep] = useState('goal');
  
  // Goal step state
  const [selectedIntent, setSelectedIntentState] = useState(safeResults.selectedIntent);
  const [customIntent, setCustomIntentState] = useState('');
  const [userDirectivesText, setUserDirectivesTextState] = useState('');
  const [targetPlatform, setTargetPlatformState] = useState('');
  const [customPlatform, setCustomPlatformState] = useState('');
  const [selectedContentType, setSelectedContentTypeState] = useState(safeResults.detectedContentType || '');
  const [customContentType, setCustomContentTypeState] = useState('');
  
  // Questions step state
  const [questionAnswers, setQuestionAnswersState] = useState({});
  const [lastQuestionAnswers, setLastQuestionAnswers] = useState({});
  
  // Analysis results state
  const [localResults, setLocalResults] = useState(safeResults);
  const [cachedIntents, setCachedIntents] = useState(safeResults.suggestedIntents);
  const [cachedReferenceEditingPatterns, setCachedReferenceEditingPatterns] = useState(safeResults.referenceEditingPatterns || null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasEverHadQuestions, setHasEverHadQuestions] = useState(safeResults.probingQuestions.length > 0);
  const [workingQuestions, setWorkingQuestions] = useState(safeResults.probingQuestions);
  
  // Track if user has interacted with form to prevent overwriting user input
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Edit Like Me state
  const [selectedReferenceProject, setSelectedReferenceProject] = useState(null);
  const [referenceProjectData, setReferenceProjectData] = useState(null);
  const cachedPatternsRef = useRef(null); // Ref for synchronous access in callbacks
  const [reviewedPatterns, setReviewedPatterns] = useState(null);
  // ELM v2: synthesis state
  const [synthesisResult, setSynthesisResult] = useState(null);
  const [synthesisProgressMessage, setSynthesisProgressMessage] = useState(null);

  // Pattern Review step state (Edit Like Me only)
  const [modifiedPatterns, setModifiedPatterns] = useState(null);

  // Change tracking
  const [lastIntent, setLastIntent] = useState(selectedIntent);
  const [lastDirectives, setLastDirectives] = useState(userDirectivesText);
  const [lastTargetPlatform, setLastTargetPlatform] = useState(targetPlatform);
  const [lastContentType, setLastContentType] = useState(selectedContentType);

  // Plan selections
  const [planSelections, setPlanSelections] = useState(() => {
    const initial = {};
    safeResults.plan.forEach(chip => {
      initial[chip.chipLabel] = chipSelectedByDefault(chip);
    });
    return initial;
  });
  const activePlan = isEditLikeMeMode && Array.isArray(synthesisResult?.plan)
    ? synthesisResult.plan
    : (Array.isArray(localResults.plan) ? localResults.plan : EMPTY_PLAN);

  /**
   * Updates results while preserving cached intents
   * @param {AnalysisResults} newResults - New analysis results
   */
  const updateResultsWithCachedIntents = useCallback((newResults) => {
    if (!isMountedRef.current) return; // Prevent state updates after unmount

    // Cache reference editing patterns from analysis response
    if (newResults.referenceEditingPatterns && isEditLikeMeMode) {
      setCachedReferenceEditingPatterns(newResults.referenceEditingPatterns);
      cachedPatternsRef.current = newResults.referenceEditingPatterns;
    }

    // Always ensure we have a valid suggestedIntents array
    const ensureValidIntents = (intents) => Array.isArray(intents) ? intents : [];
    
    if (newResults.probingQuestions && newResults.probingQuestions.length > 0) {
      // In ELM mode, don't let regular probingQuestions trigger the "Refine" step.
      // Only synthesis style choices should set hasEverHadQuestions (done in handleNext).
      if (!isEditLikeMeMode) {
        setHasEverHadQuestions(true);
      }
      setWorkingQuestions(newResults.probingQuestions);
    } else if (workingQuestions.length === 0 && newResults.probingQuestions.length === 0) {
      setWorkingQuestions([]);
    }
    
    // Always preserve a valid suggestedIntents array
    let finalResults;
    if (newResults.suggestedIntents && newResults.suggestedIntents.length > 0) {
      setCachedIntents(newResults.suggestedIntents);
      finalResults = {
        ...newResults,
        suggestedIntents: ensureValidIntents(newResults.suggestedIntents)
      };
    } else {
      finalResults = {
        ...newResults,
        suggestedIntents: ensureValidIntents(cachedIntents)
      };
    }

    // Cache and preserve referenceEditingPatterns across result updates
    // (subsequent wizard steps may return results without patterns)
    if (newResults.referenceEditingPatterns && typeof newResults.referenceEditingPatterns === 'object' && Object.keys(newResults.referenceEditingPatterns).length > 0) {
      setCachedReferenceEditingPatterns(newResults.referenceEditingPatterns);
      finalResults.referenceEditingPatterns = newResults.referenceEditingPatterns;
      console.log('useAnalysisWizard: Cached referenceEditingPatterns with keys:', Object.keys(newResults.referenceEditingPatterns).join(', '));
    } else if (cachedPatternsRef.current) {
      finalResults.referenceEditingPatterns = cachedPatternsRef.current;
      console.log('useAnalysisWizard: Restored cached referenceEditingPatterns');
    } else {
      console.log('useAnalysisWizard: No referenceEditingPatterns in results or cache');
    }
    // Also preserve referenceEditingPatternsUrl
    if (newResults.referenceEditingPatternsUrl) {
      finalResults.referenceEditingPatternsUrl = newResults.referenceEditingPatternsUrl;
    }

    setLocalResults(finalResults);
  }, [workingQuestions.length, cachedIntents, isEditLikeMeMode]);

  // Update cached intents when results prop changes
  useEffect(() => {
    if (results && results.suggestedIntents && results.suggestedIntents.length > 0) {
      setCachedIntents(results.suggestedIntents);
      setLocalResults(results);
    }
  }, [results]);

  // ADD: Sync individual form states when results change (only if user hasn't interacted)
  useEffect(() => {
    if (results && !hasUserInteracted) {
      // Only update if the current state is empty/default and results have data
      if (!selectedIntent && results.selectedIntent) {
        setSelectedIntentState(results.selectedIntent);
      }
      if (!selectedContentType && results.detectedContentType) {
        setSelectedContentTypeState(results.detectedContentType);
      }
      // Update working questions if they've changed
      if (results.probingQuestions && results.probingQuestions.length > 0) {
        setWorkingQuestions(results.probingQuestions);
        setHasEverHadQuestions(true);
      }
    }
  }, [results]);

  // Update plan selections when the active plan changes.
  // In ELM mode, the synthesized plan is the source of truth once it's available.
  useEffect(() => {
    setPlanSelections(prev => {
      const updated = { ...prev };
      let didChange = false;
      activePlan.forEach(chip => {
        if (!(chip.chipLabel in updated)) {
          updated[chip.chipLabel] = chipSelectedByDefault(chip);
          didChange = true;
        }
      });
      return didChange ? updated : prev;
    });
  }, [activePlan]);

  // Auto-skip questions step if no working questions are present AND user has never had questions
  useEffect(() => {
    if (currentStep === 'questions' && workingQuestions.length === 0 && !hasEverHadQuestions) {
      setCurrentStep('plan');
    }
  }, [currentStep, workingQuestions.length, hasEverHadQuestions]);

  /**
   * Gets the current step index for progress display
   * @returns {number}
   */
  const getStepIndex = () => {
    if (currentStep === 'goal') return 0;
    // In Edit Like Me mode, steps are [Set Up, (Refine), Plan]
    if (isEditLikeMeMode) {
      if (currentStep === 'questions') return 1;
      if (currentStep === 'patternReview') return hasEverHadQuestions ? 2 : 1;
      return 0;
    }
    if (currentStep === 'questions') return 1;
    if (currentStep === 'plan') return hasEverHadQuestions ? 2 : 1;
    return 0;
  };

  /**
   * Determines if user can proceed to next step
   * @returns {boolean}
   */
  const canProceed = () => {
    if (currentStep === 'goal') {
      // In Edit Like Me mode, only require a reference project selection
      if (isEditLikeMeMode) {
        return !!selectedReferenceProject;
      }
      const intentValue = selectedIntent === 'custom' ? customIntent : selectedIntent;
      const hasIntent = !!intentValue && intentValue.trim().length > 0;
      const platformValue = targetPlatform === 'Other' ? customPlatform : targetPlatform;
      const hasPlatform = !!platformValue && platformValue.trim().length > 0;
      return hasIntent && hasPlatform;
    }
    if (currentStep === 'patternReview') return true; // Always allow approval
    if (currentStep === 'questions') return !isRefreshing;
    return true;
  };

  const getQuestionAnswersArray = useCallback(() => {
    const questionsById = {};
    workingQuestions.forEach(question => {
      questionsById[question.id] = question;
    });

    return Object.entries(questionAnswers).map(([questionId, answer]) => {
      const question = questionsById[questionId];
      return {
        questionId,
        answer,
        question: question?.text,
        planItemIndex: question?.planItemIndex,
        category: question?.category,
        additionType: question?.additionType,
        transcriptEvidence: question?.transcriptEvidence,
      };
    });
  }, [questionAnswers, workingQuestions]);

  /**
   * Handles navigation to next step
   */
  const handleNext = async () => {
    if (currentStep === 'goal') {
      // In Edit Like Me mode, use defaults for hidden fields
      const platformValue = isEditLikeMeMode ? 'general' : (targetPlatform === 'Other' ? customPlatform : targetPlatform);
      const intentValue = isEditLikeMeMode ? 'edit-like-reference' : (selectedIntent === 'custom' ? customIntent : selectedIntent);
      const contentTypeValue = isEditLikeMeMode ? 'general' : (selectedContentType === 'Custom' ? customContentType : selectedContentType);
      let updatedResults = localResults;

      // In Edit Like Me mode, if we already have cached patterns from the initial analysis,
      // skip the refresh and go straight to pattern review (use ref for synchronous access)
      if (isEditLikeMeMode && (cachedReferenceEditingPatterns || cachedPatternsRef.current)) {
        setCurrentStep('patternReview');
        return;
      }

      // Always refresh in Edit Like Me mode (need to send reference data) or when fields changed
      const needsRefresh = isEditLikeMeMode ||
          intentValue !== lastIntent ||
          userDirectivesText !== lastDirectives ||
          platformValue !== lastTargetPlatform ||
          contentTypeValue !== lastContentType;

      if (needsRefresh) {
        setIsRefreshing(true);
        try {
          // Convert intent code to label for backend analysis
          const intentLabel = isEditLikeMeMode ? 'edit-like-reference' : (selectedIntent === 'custom' ? customIntent : getIntentLabel(selectedIntent, cachedIntents));

          const feedback = {
            chipUpdates: [],
            questionAnswers: [],
            newDirectives: isEditLikeMeMode ? [] : userDirectivesText.split('\n').filter(line => line.trim() !== ''),
            feedback: `Intent: ${intentLabel}, Content Type: ${contentTypeValue}`,
            targetPlatform: platformValue,
            selectedIntent: intentLabel,
            selectedContentType: contentTypeValue,
            userFlow: {
              hasSeenQuestions: false,
              isAnsweringQuestions: false,
              isUpdatingGoals: true
            }
          };

          // Create analysis request
          const analysisRequest = {
            transcript,
            transcriptionId: transcriptId,
            projectId,
            selectedIntent: intentLabel,
            userDirectives: isEditLikeMeMode ? [] : userDirectivesText.split('\n').filter(line => line.trim() !== ''),
            targetPlatform: platformValue,
            selectedContentType: contentTypeValue,
            userFlow: feedback.userFlow,
            locale: navigator.language || 'en'
          };

          // Always include Edit Like Me flags when in that mode
          if (isEditLikeMeMode) {
            analysisRequest.isEditLikeMeMode = true;
            if (selectedReferenceProject) {
              analysisRequest.referenceProjectId = selectedReferenceProject;
            }
          }

          const data = await deepCutApi.analyzeTranscript(analysisRequest);
          updateResultsWithCachedIntents(data);
          updatedResults = data;
          
          // Update tracking variables
          setLastIntent(intentValue);
          setLastDirectives(userDirectivesText);
          setLastTargetPlatform(platformValue);
          setLastContentType(contentTypeValue);
        } catch (error) {
          console.error('Failed to update analysis:', error);
          if (isMountedRef.current) {
            setIsRefreshing(false);
          }
          // Don't proceed — let user retry
          return;
        }
      }

      // Navigate to next step
      // In Edit Like Me mode: extraction → synthesis → quickChoices/patternReview
      const patternsAvailable = isEditLikeMeMode && (
        updatedResults.referenceEditingPatterns ||
        cachedPatternsRef.current
      );
      console.log('[ELM-WIZARD] Navigation: isEditLikeMeMode=', isEditLikeMeMode, 'patternsAvailable=', patternsAvailable, 'hasEverHadQuestions=', hasEverHadQuestions);
      if (patternsAvailable) {
        // Cache patterns from the analysis response
        const patterns = updatedResults.referenceEditingPatterns || cachedPatternsRef.current;
        if (updatedResults.referenceEditingPatterns && !cachedPatternsRef.current) {
          setCachedReferenceEditingPatterns(updatedResults.referenceEditingPatterns);
          cachedPatternsRef.current = updatedResults.referenceEditingPatterns;
        }

        // ELM v2: call synthesis to merge patterns with transcript analysis
        try {
          console.log('[ELM-WIZARD] Starting synthesis...');
          setSynthesisProgressMessage((window.getI18n && window.getI18n(ti18n, 'SYNTHESIS_COMPARING_TRANSCRIPT')) || 'Comparing with your transcript...');
          const synthResponse = await deepCutApi.synthesizeWithReference(patterns, transcript);
          console.log('[ELM-WIZARD] Synthesis response:', { timedOut: synthResponse.synthesisTimedOut, hasResult: !!synthResponse.synthesisResult, styleChoices: synthResponse.synthesisResult?.styleChoices?.length || 0 });

          if (synthResponse.synthesisTimedOut) {
            // Fallback to v1 flow — show raw patterns
            console.warn('[ELM-WIZARD] Synthesis timed out, falling back to v1 pattern review');
            setIsRefreshing(false);
            setCurrentStep('patternReview');
            return;
          }

          const sr = synthResponse.synthesisResult;
          setSynthesisResult(sr);
          setSynthesisProgressMessage(null);
          setIsRefreshing(false);

          // If synthesis produced style choices, show them as questions
          if (sr?.styleChoices && sr.styleChoices.length > 0) {
            console.log('[ELM-WIZARD] Synthesis has', sr.styleChoices.length, 'style choices → showing questions step');
            // Also include questions from additionGuidance items that need style choices
            const additionQuestions = (sr.additionGuidance || [])
              .filter(ag => ag.needsStyleChoice && ag.styleChoiceQuestion)
              .map((ag, idx) => ({
                id: 'addition_' + ag.type + '_' + idx,
                text: ag.styleChoiceQuestion,
                questionType: ag.styleChoiceOptions ? 'multiple-choice' : 'open-ended',
                options: ag.styleChoiceOptions || undefined,
                transcriptEvidence: ag.guidance || '',
                additionType: ag.type,
                category: 'addition',
              }));

            const synthesisQuestions = sr.styleChoices.map(sc => ({
              id: sc.id,
              text: sc.question,
              questionType: sc.type === 'multiple-choice' ? 'multiple-choice' : 'open-ended',
              options: sc.options,
              transcriptEvidence: sc.transcriptEvidence,
              planItemIndex: sc.planItemIndex,
              additionType: sc.additionType || undefined,
              category: sc.category || 'deletion',
            })).concat(additionQuestions);
            setWorkingQuestions(synthesisQuestions);
            setHasEverHadQuestions(true);
            setCurrentStep('questions');
          } else {
            // No style choices — style maps cleanly, go straight to pattern review
            // Important: do NOT set hasEverHadQuestions here — we skip "Refine" step entirely
            console.log('[ELM-WIZARD] No style choices → skipping questions, going to patternReview');
            setCurrentStep('patternReview');
          }
        } catch (synthError) {
          console.error('[ELM-WIZARD] Synthesis failed:', synthError.message, '→ falling back to patternReview');
          setSynthesisProgressMessage(null);
          setIsRefreshing(false);
          setCurrentStep('patternReview');
        }
        return;
      }

      setIsRefreshing(false);
      if (hasEverHadQuestions || (updatedResults.probingQuestions && updatedResults.probingQuestions.length > 0)) {
        setCurrentStep('questions');
      } else {
        setCurrentStep('plan');
      }
    } else if (currentStep === 'patternReview') {
      // Pattern review IS the plan in ELM mode — no further navigation
      // Execution is handled by the Execute Plan button in the footer
    } else if (currentStep === 'questions') {
      // Check if answers have changed and we need to refresh
      const answersChanged = JSON.stringify(questionAnswers) !== JSON.stringify(lastQuestionAnswers);
      
      if (answersChanged) {
        setIsRefreshing(true);
        try {
          const questionAnswersArray = getQuestionAnswersArray();

          if (isEditLikeMeMode) {
            const refPatterns = cachedPatternsRef.current || localResults.referenceEditingPatterns || cachedReferenceEditingPatterns || null;
            if (!refPatterns || !synthesisResult) {
              throw new Error('Missing reference patterns for Edit Like Me refinement');
            }

            const synthResponse = await deepCutApi.synthesizeWithReference(
              refPatterns,
              transcript,
              questionAnswersArray,
              synthesisResult,
              2
            );

            if (synthResponse.synthesisTimedOut) {
              throw new Error('Synthesis refinement timed out');
            }

            setSynthesisResult(synthResponse.synthesisResult);
            setLastQuestionAnswers({ ...questionAnswers });
          } else {
            const feedback = {
              chipUpdates: [],
              questionAnswers: questionAnswersArray,
              newDirectives: [],
              userFlow: {
                hasSeenQuestions: true,
                isAnsweringQuestions: true,
                isUpdatingGoals: false
              }
            };

            // Convert intent code to label for backend analysis
            const intentLabel = selectedIntent === 'custom' ? customIntent : getIntentLabel(selectedIntent, cachedIntents);

            const analysisRequest = {
              transcript,
              selectedIntent: intentLabel,
              userDirectives: userDirectivesText.split('\n').filter(line => line.trim() !== ''),
              targetPlatform: targetPlatform === 'Other' ? customPlatform : targetPlatform,
              selectedContentType: selectedContentType === 'Custom' ? customContentType : selectedContentType,
              userFlow: feedback.userFlow,
              locale: navigator.language || 'en'
            };

            const data = await deepCutApi.updateAnalysis(analysisRequest, feedback);
            updateResultsWithCachedIntents(data);
            setLastQuestionAnswers({ ...questionAnswers });
          }
        } catch (error) {
          console.error('Failed to update plan based on answers:', error);
          if (window.alertErrorMessage) {
            window.alertErrorMessage(error.message || error);
          }
          return;
        } finally {
          setIsRefreshing(false);
        }
      }
      
      // In ELM mode, go to patternReview after questions (not the old plan step)
      setCurrentStep(isEditLikeMeMode ? 'patternReview' : 'plan');
    }
  };

  /**
   * Handles navigation to previous step
   */
  const handleBack = () => {
    if (currentStep === 'plan') {
      if (hasEverHadQuestions) {
        setCurrentStep('questions');
      } else {
        setCurrentStep('goal');
      }
    } else if (currentStep === 'patternReview') {
      if (hasEverHadQuestions) {
        setCurrentStep('questions');
      } else {
        setCurrentStep('goal');
      }
    } else if (currentStep === 'questions') {
      setCurrentStep('goal');
    }
  };

  /**
   * Handles approval of the pattern review step
   * @param {Object} patternReviewOutput - The output from PatternReviewStep
   */
  const handlePatternReviewApprove = useCallback((patternReviewOutput) => {
    // Replace cached patterns with user-modified patterns
    setCachedReferenceEditingPatterns(patternReviewOutput.referenceEditingPatterns);
    cachedPatternsRef.current = patternReviewOutput.referenceEditingPatterns; // Sync ref for immediate access
    setReviewedPatterns(patternReviewOutput);
    // In ELM mode, pattern review IS the plan — don't navigate, caller handles execution
  }, []);

  /**
   * Handles applying the final editing plan
   */
  const handleApply = async () => {
    try {
      // Create user feedback with selected chips
      const plan = activePlan;
      const chipUpdates = plan
        .filter(chip => planSelections[chip.chipLabel] ?? chipSelectedByDefault(chip))
        .map(chip => ({
          chipLabel: chip.chipLabel,
          displayLabel: chip.displayLabel,
          action: 'remove'
        }));

      const questionAnswersArray = getQuestionAnswersArray();

      // Normalize values for ELM mode (match handleNext normalization)
      const intentLabel = isEditLikeMeMode
        ? 'edit-like-reference'
        : (selectedIntent === 'custom' ? customIntent : getIntentLabel(selectedIntent, cachedIntents));
      const platformValue = isEditLikeMeMode
        ? 'general'
        : (targetPlatform === 'Other' ? customPlatform : targetPlatform);
      const contentTypeValue = isEditLikeMeMode
        ? 'general'
        : (selectedContentType === 'Custom' ? customContentType : selectedContentType);

      // Get reference editing patterns (ref for sync access after approve())
      const refPatterns = cachedPatternsRef.current || localResults.referenceEditingPatterns || cachedReferenceEditingPatterns || null;

      const feedback = {
        chipUpdates,
        plan,
        probingQuestions: localResults.probingQuestions || [],
        strategySummary: localResults.strategySummary || '',
        questionAnswers: questionAnswersArray,
        userDirectives: userDirectivesText.split('\n').filter(line => line.trim() !== ''),
        targetPlatform: platformValue,
        selectedIntent: intentLabel,
        selectedContentType: contentTypeValue,
        userFlow: {
          hasSeenQuestions: hasEverHadQuestions,
          isAnsweringQuestions: false,
          isUpdatingGoals: false,
          isEditLikeMeMode,
        },
        // Only forward the reference project in Edit Like Me mode; the DeepCut flow never uses it.
        referenceProjectId: isEditLikeMeMode ? (selectedReferenceProject || null) : null,
        isEditLikeMeMode: isEditLikeMeMode || false,
        referenceEditingPatterns: refPatterns,
        referenceEditingPatternsUrl: localResults.referenceEditingPatternsUrl || null,
        // ELM v2: pass synthesis addition guidance for the addition detector
        additionGuidance: Array.isArray(synthesisResult?.additionGuidance) ? synthesisResult.additionGuidance : null,
      };

      console.log('handleApply: ELM mode =', isEditLikeMeMode, ', plan chips =', plan.length, ', refPatterns =', !!refPatterns, ', hasAdditionGuidance =', !!feedback.additionGuidance);
      onApply(feedback);
    } catch (error) {
      console.error('Failed to apply editing plan:', error);
    }
  };

  return {
    // Step management
    currentStep,
    stepIndex: getStepIndex(),
    hasEverHadQuestions,
    
    // Goal step
    selectedIntent,
    setSelectedIntent: (value) => {
      setHasUserInteracted(true);
      setSelectedIntentState(value);
    },
    customIntent,
    setCustomIntent: (value) => {
      setHasUserInteracted(true);
      setCustomIntentState(value);
    },
    userDirectivesText,
    setUserDirectivesText: (value) => {
      setHasUserInteracted(true);
      setUserDirectivesTextState(value);
    },
    targetPlatform,
    setTargetPlatform: (value) => {
      setHasUserInteracted(true);
      setTargetPlatformState(value);
    },
    customPlatform,
    setCustomPlatform: (value) => {
      setHasUserInteracted(true);
      setCustomPlatformState(value);
    },
    selectedContentType,
    setSelectedContentType: (value) => {
      setHasUserInteracted(true);
      setSelectedContentTypeState(value);
    },
    customContentType,
    setCustomContentType: (value) => {
      setHasUserInteracted(true);
      setCustomContentTypeState(value);
    },
    
    // Questions step
    workingQuestions,
    questionAnswers,
    setQuestionAnswers: (value) => {
      setHasUserInteracted(true);
      setQuestionAnswersState(value);
    },
    
    // Plan step
    localResults,
    planSelections,
    setPlanSelections,
    
    // Navigation
    canProceed,
    handleNext,
    handleBack,
    handleApply,
    
    // State
    isRefreshing,

    // Pattern Review (Edit Like Me)
    modifiedPatterns,
    setModifiedPatterns,
    cachedReferenceEditingPatterns,

    // Edit Like Me
    isEditLikeMeMode,
    selectedReferenceProject,
    setSelectedReferenceProject,
    referenceProjectData,
    setReferenceProjectData,
    handleReferenceProjectChange: (projectId, projectData) => {
      setSelectedReferenceProject(projectId);
      setReferenceProjectData(projectData);
      // Clear stale patterns from previous reference project — all sources
      setCachedReferenceEditingPatterns(null);
      cachedPatternsRef.current = null;
      setReviewedPatterns(null);
      // Clear patterns from localResults so stale data can't leak through on refresh failure
      if (localResults?.referenceEditingPatterns) {
        setLocalResults(prev => ({ ...prev, referenceEditingPatterns: null }));
      }
      // Reset question tracking so the stepper doesn't show a stale "Refine" step
      setHasEverHadQuestions(false);
      // Clear question/review state from previous reference project
      setQuestionAnswersState({});
      setLastQuestionAnswers({});
      setWorkingQuestions([]);
      setModifiedPatterns(null);
      // Clear synthesis state so the next run doesn't reuse previous project's results
      setSynthesisResult(null);
      setSynthesisProgressMessage(null);
      // Clear backend session UID so the next request doesn't reuse the old reference context
      deepCutApi.clearReferenceRequestUID();
    },

    // Pattern Review
    reviewedPatterns,
    handlePatternReviewApprove,

    // ELM v2: Synthesis
    synthesisResult,
    synthesisProgressMessage,
  };
}
